// Pure helpers for chat photos, videos and files: what a picked file is,
// how far a photo is downscaled before upload, removing location from MP4 /
// QuickTime video, and how attachments read in the inbox and thread.
import type { Schemas } from "@10twenty/api";
import type { Message } from "./chat";

export type Media = Schemas["Media"];
export type MediaKind = Media["kind"];
export type MediaLimits = NonNullable<Schemas["Capabilities"]["media"]>;

/** Photos are downscaled in the browser to this many pixels on the longer side. */
export const IMAGE_MAX_PX = 2048;
/** Video poster frames (the server makes its 320 px thumbnail from them). */
export const POSTER_MAX_PX = 640;
export const IMAGE_QUALITY = 0.85;

// --------------------------------------------------------------- picking

const IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
]);
const VIDEO_TYPES = new Set(["video/mp4", "video/quicktime", "video/webm"]);

/** How a picked file is sent: photos and videos by type, anything else as a file. */
export function mediaKindOf(file: { type: string; name: string }): MediaKind {
  const type = file.type.toLowerCase();
  if (IMAGE_TYPES.has(type)) return "image";
  if (VIDEO_TYPES.has(type)) return "video";
  // Some systems leave the type empty; go by the extension then.
  if (type === "") {
    const ext = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
    if (ext && ["jpg", "jpeg", "png", "gif", "webp", "avif", "heic", "heif"].includes(ext)) return "image";
    if (ext && ["mp4", "m4v", "mov", "webm"].includes(ext)) return "video";
  }
  return "file";
}

export function maxBytes(kind: MediaKind, limits: MediaLimits): number {
  return kind === "image" ? limits.image_max_bytes : kind === "video" ? limits.video_max_bytes : limits.file_max_bytes;
}

/** "820 KB", "4.2 MB" (decimal units). */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes;
  let unit = "B";
  for (const next of units) {
    if (value < 1000) break;
    value /= 1000;
    unit = next;
  }
  return `${value < 10 ? value.toFixed(1).replace(/\.0$/, "") : Math.round(value)} ${unit}`;
}

export interface Picked<F> {
  file: F;
  kind: MediaKind;
}

/**
 * Which of `files` fit beside `existing` attachments, and a notice for those
 * that don't: over the per-message count, or (videos and files, whose size
 * the browser won't change) over their kind's limit. Photos are checked
 * after downscaling.
 */
export function pickFiles<F extends { type: string; name: string; size: number }>(
  files: readonly F[],
  existing: number,
  limits: MediaLimits,
): { accepted: Picked<F>[]; notice: string | null } {
  const accepted: Picked<F>[] = [];
  const tooBig: string[] = [];
  let tooMany = 0;
  for (const file of files) {
    const kind = mediaKindOf(file);
    if (kind !== "image" && file.size > maxBytes(kind, limits)) {
      tooBig.push(`${file.name || "This file"} is over ${formatBytes(maxBytes(kind, limits))}`);
      continue;
    }
    if (existing + accepted.length >= limits.attachments_max) {
      tooMany++;
      continue;
    }
    accepted.push({ file, kind });
  }
  const notices = [...tooBig];
  if (tooMany > 0) notices.push(`A message can have up to ${limits.attachments_max} attachments`);
  return { accepted, notice: notices.length ? `${notices.join(". ")}.` : null };
}

// -------------------------------------------------------------- resizing

export interface Size {
  width: number;
  height: number;
}

/** `size` shrunk (never enlarged) so its longer side is at most `max`, keeping its shape. */
export function fitWithin(size: Size, max: number): Size {
  const longer = Math.max(size.width, size.height);
  if (longer <= max) return { width: size.width, height: size.height };
  const scale = max / longer;
  return {
    width: size.width >= size.height ? max : Math.max(1, Math.round(size.width * scale)),
    height: size.height > size.width ? max : Math.max(1, Math.round(size.height * scale)),
  };
}

/**
 * The sizes to draw through on the way from `from` to `to`: halving while
 * that stays at least `to`, since one big step aliases. Ends with `to`.
 */
export function downscaleSteps(from: Size, to: Size): Size[] {
  const steps: Size[] = [];
  let { width, height } = from;
  while (width / 2 > to.width && height / 2 > to.height) {
    width = Math.round(width / 2);
    height = Math.round(height / 2);
    steps.push({ width, height });
  }
  steps.push({ width: to.width, height: to.height });
  return steps;
}

/**
 * The types to try when re-encoding a photo: WebP first (it keeps
 * transparency), then PNG for PNG sources (alpha) or JPEG. Canvas encoding
 * writes no metadata, so EXIF (location, camera) is gone either way.
 */
export function encodeTypes(sourceType: string): string[] {
  return ["image/webp", sourceType === "image/png" ? "image/png" : "image/jpeg"];
}

/**
 * Whether a photo goes up without re-encoding: animations (GIF, animated
 * WebP), which a canvas would flatten. Their metadata is stripped instead
 * (`stripAnimationMetadata`).
 */
export function keepsOriginal(type: string, head: Uint8Array): boolean {
  if (type === "image/gif") return true;
  return type === "image/webp" && isAnimatedWebp(head);
}

/** A photo's type from its first bytes (GIF, WebP, PNG, JPEG), for files the system gave none. */
export function sniffImageType(head: Uint8Array): string | null {
  if (head.length >= 6 && /^GIF8[79]a$/.test(String.fromCharCode(...head.subarray(0, 6)))) return "image/gif";
  if (head.length >= 12 && fourcc(head, 0) === "RIFF" && fourcc(head, 8) === "WEBP") return "image/webp";
  if (head.length >= 8 && head[0] === 0x89 && fourcc(head, 1).startsWith("PNG")) return "image/png";
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return "image/jpeg";
  return null;
}

/** A WebP whose VP8X header sets the animation flag (needs the first 21 bytes). */
export function isAnimatedWebp(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 21 &&
    fourcc(bytes, 0) === "RIFF" &&
    fourcc(bytes, 8) === "WEBP" &&
    fourcc(bytes, 12) === "VP8X" &&
    (bytes[20]! & 0x02) !== 0
  );
}

export class ImageMetadataError extends Error {
  constructor() {
    super("This image can't be read");
    this.name = "ImageMetadataError";
  }
}

/**
 * A WebP without its `EXIF` and `XMP ` chunks (and their VP8X flags), so an
 * animation that goes up as it is carries no location. Throws
 * `ImageMetadataError` on a malformed file.
 */
export function stripWebpMetadata(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  if (bytes.length < 12 || fourcc(bytes, 0) !== "RIFF" || fourcc(bytes, 8) !== "WEBP") throw new ImageMetadataError();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const end = Math.min(bytes.length, 8 + view.getUint32(4, true));
  const kept: Uint8Array[] = [];
  let at = 12;
  while (at < end) {
    if (at + 8 > end) throw new ImageMetadataError();
    const type = fourcc(bytes, at);
    const size = view.getUint32(at + 4, true);
    const next = at + 8 + size + (size % 2);
    if (at + 8 + size > end) throw new ImageMetadataError();
    if (type !== "EXIF" && type !== "XMP ") {
      const chunk = bytes.slice(at, Math.min(next, end));
      // VP8X flags: 0x08 EXIF, 0x04 XMP.
      if (type === "VP8X" && chunk.length > 8) chunk[8] = chunk[8]! & ~0x0c;
      kept.push(chunk);
    }
    at = next;
  }
  const out = new Uint8Array(12 + kept.reduce((sum, chunk) => sum + chunk.length, 0));
  out.set(bytes.subarray(0, 12));
  new DataView(out.buffer).setUint32(4, out.length - 8, true);
  let offset = 12;
  for (const chunk of kept) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/** GIF application extensions an animation needs (looping); any other (XMP, ICC, …) is dropped. */
const GIF_APPLICATIONS = new Set(["NETSCAPE2.0", "ANIMEXTS1.0"]);

/**
 * A GIF without comments and application extensions other than looping
 * (where XMP lives), frames untouched. Throws `ImageMetadataError` on a
 * malformed file.
 */
export function stripGifMetadata(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const at = (i: number) => {
    if (i >= bytes.length) throw new ImageMetadataError();
    return bytes[i]!;
  };
  const header = String.fromCharCode(...bytes.subarray(0, 6));
  if (header !== "GIF87a" && header !== "GIF89a") throw new ImageMetadataError();
  const colours = (packed: number) => (packed & 0x80 ? 3 * 2 ** ((packed & 0x07) + 1) : 0);
  /** Offset past the data sub-blocks starting at `i`. */
  const skipBlocks = (i: number) => {
    while (at(i) !== 0) i += at(i) + 1;
    return i + 1;
  };
  const parts: Uint8Array[] = [];
  let i = 13 + colours(at(10));
  parts.push(bytes.subarray(0, i));
  for (;;) {
    const start = i;
    const block = at(i);
    if (block === 0x3b) {
      parts.push(bytes.subarray(i, i + 1));
      break;
    }
    if (block === 0x2c) {
      i += 10 + colours(at(i + 9));
      i = skipBlocks(i + 1);
      parts.push(bytes.subarray(start, i));
    } else if (block === 0x21) {
      const label = at(i + 1);
      i = skipBlocks(i + 2);
      let keep = label !== 0xfe;
      if (label === 0xff) {
        const id = at(start + 2) === 11 ? String.fromCharCode(...bytes.subarray(start + 3, start + 14)) : "";
        keep = GIF_APPLICATIONS.has(id);
      }
      if (keep) parts.push(bytes.subarray(start, i));
    } else {
      throw new ImageMetadataError();
    }
  }
  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** An animation that goes up as it is, without its metadata. */
export function stripAnimationMetadata(bytes: Uint8Array, type: string): Uint8Array<ArrayBuffer> {
  return type === "image/gif" ? stripGifMetadata(bytes) : stripWebpMetadata(bytes);
}

// ------------------------------------------------- video location metadata

function fourcc(bytes: Uint8Array, at: number): string {
  return String.fromCharCode(bytes[at]!, bytes[at + 1]!, bytes[at + 2]!, bytes[at + 3]!);
}

export class VideoMetadataError extends Error {
  constructor(message = "This video can't be read") {
    super(message);
    this.name = "VideoMetadataError";
  }
}

interface BoxHeader {
  type: string;
  /** Offset of the box. */
  start: number;
  /** Offset of its payload. */
  body: number;
  /** Offset just past it. */
  end: number;
}

function readBox(bytes: Uint8Array, at: number, limit: number): BoxHeader {
  if (at + 8 > limit) throw new VideoMetadataError();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let size = view.getUint32(at);
  let body = at + 8;
  if (size === 1) {
    if (at + 16 > limit) throw new VideoMetadataError();
    size = Number(view.getBigUint64(at + 8));
    body = at + 16;
  } else if (size === 0) {
    size = limit - at;
  }
  const end = at + size;
  if (end > limit || end < body) throw new VideoMetadataError();
  return { type: fourcc(bytes, at + 4), start: at, body, end };
}

/** Location boxes: QuickTime/Android `©xyz` and 3GPP `loci`. */
const LOCATION_BOXES = new Set(["©xyz", "loci"]);
/** Adobe XMP (may hold GPS) as a `udta` box or a `uuid` box with the XMP UUID. */
const XMP_BOX = "XMP_";
const XMP_UUID = "be7acfcb97a942e89c71999491e3afac";
/** Boxes whose children may hold location metadata. */
const CONTAINERS = new Set(["moov", "trak", "udta", "meta", "ilst"]);

/** Turns a box into `free` padding with its payload zeroed; its size (so every offset in the file) stays. */
function wipe(bytes: Uint8Array, box: BoxHeader): void {
  bytes.set([0x66, 0x72, 0x65, 0x65], box.start + 4); // "free"
  bytes.fill(0, box.body, box.end);
}

function isXmpUuid(bytes: Uint8Array, box: BoxHeader): boolean {
  if (box.type !== "uuid" || box.body + 16 > box.end) return false;
  let hex = "";
  for (let i = 0; i < 16; i++) hex += bytes[box.body + i]!.toString(16).padStart(2, "0");
  return hex === XMP_UUID;
}

/**
 * QuickTime `meta` has its children right after the header, ISO `meta` (a
 * full box) after four bytes of version and flags; a child's size is never 0.
 */
function metaChildren(bytes: Uint8Array, box: BoxHeader): number {
  if (box.body + 4 <= box.end && bytes[box.body] === 0 && bytes[box.body + 1] === 0) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(box.body) === 0) return box.body + 4;
  }
  return box.body;
}

/** The 1-based indices of `keys` entries naming a location (`com.apple.quicktime.location.ISO6709` and the like). */
function locationKeys(bytes: Uint8Array, keys: BoxHeader): Set<number> {
  const found = new Set<number>();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (keys.body + 8 > keys.end) throw new VideoMetadataError();
  const count = view.getUint32(keys.body + 4);
  let at = keys.body + 8;
  for (let index = 1; index <= count; index++) {
    if (at + 8 > keys.end) throw new VideoMetadataError();
    const size = view.getUint32(at);
    if (size < 8 || at + size > keys.end) throw new VideoMetadataError();
    let name = "";
    for (let i = at + 8; i < at + size; i++) name += String.fromCharCode(bytes[i]!);
    if (/location|iso6709/i.test(name)) found.add(index);
    at += size;
  }
  return found;
}

/** Zeroes the values of an `ilst` item's `data` boxes (after their type and locale), keeping the structure. */
function clearItemValues(bytes: Uint8Array, item: BoxHeader): void {
  let at = item.body;
  while (at < item.end) {
    const child = readBox(bytes, at, item.end);
    if (child.type === "data") bytes.fill(0, Math.min(child.body + 8, child.end), child.end);
    else bytes.fill(0, child.body, child.end);
    at = child.end;
  }
}

function cleanChildren(bytes: Uint8Array, start: number, end: number, keyed: Set<number> | null): number {
  let removed = 0;
  // Which `ilst` item numbers are locations, from this level's `keys` (wherever it stands).
  let locationItems: Set<number> | null = null;
  if (keyed === null) {
    for (let at = start; at < end;) {
      const box = readBox(bytes, at, end);
      if (box.type === "keys") locationItems = locationKeys(bytes, box);
      at = box.end;
    }
  }
  let at = start;
  while (at < end) {
    const box = readBox(bytes, at, end);
    if (LOCATION_BOXES.has(box.type) || box.type === XMP_BOX || isXmpUuid(bytes, box)) {
      wipe(bytes, box);
      removed++;
    } else if (keyed !== null) {
      // Inside an `ilst` whose items are numbered by `keys`.
      const index = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(box.start + 4);
      if (keyed.has(index)) {
        clearItemValues(bytes, box);
        removed++;
      }
    } else if (box.type === "ilst") {
      removed += cleanChildren(bytes, box.body, box.end, locationItems ?? new Set());
    } else if (box.type === "meta") {
      removed += cleanChildren(bytes, metaChildren(bytes, box), box.end, null);
    } else if (CONTAINERS.has(box.type)) {
      removed += cleanChildren(bytes, box.body, box.end, null);
    }
    at = box.end;
  }
  return removed;
}

/**
 * Removes location metadata from the boxes in `bytes` (one or more whole
 * top-level MP4/QuickTime boxes, changed in place): `©xyz` and `loci`
 * anywhere under `moov`/`trak`/`udta`/`meta`, the values of QuickTime
 * metadata items whose key names a location (ISO 6709 and its accuracy,
 * name, …), and XMP. Removed boxes become `free` padding of the same size,
 * so chunk offsets stay valid. Returns how many were removed; throws
 * `VideoMetadataError` on a malformed box.
 */
export function stripLocationBoxes(bytes: Uint8Array): number {
  return cleanChildren(bytes, 0, bytes.length, null);
}

/** Top-level boxes worth reading into memory (the media data never is). */
const TOP_LEVEL = new Set(["moov", "meta", "udta", "uuid", XMP_BOX, ...LOCATION_BOXES]);
/** A `moov` this large is not a phone video; refuse rather than read it all. */
const MAX_METADATA_BYTES = 64 * 1024 * 1024;

/**
 * `video` without location metadata (MP4/QuickTime, by its `ftyp`); other
 * containers (WebM) come back unchanged. Only the metadata boxes are read;
 * the result shares the original's media data.
 */
export async function stripVideoLocation(video: Blob): Promise<{ blob: Blob; removed: number }> {
  const head = new Uint8Array(await video.slice(0, 12).arrayBuffer());
  if (head.length < 12 || fourcc(head, 4) !== "ftyp") return { blob: video, removed: 0 };
  const parts: BlobPart[] = [];
  let removed = 0;
  let copied = 0;
  let at = 0;
  while (at < video.size) {
    const { type, size } = topLevelBox(new Uint8Array(await video.slice(at, at + 16).arrayBuffer()), video.size - at);
    if (TOP_LEVEL.has(type)) {
      if (size > MAX_METADATA_BYTES) throw new VideoMetadataError("This video's metadata is too large");
      const bytes = new Uint8Array(await video.slice(at, at + size).arrayBuffer());
      const count = stripLocationBoxes(bytes);
      if (count > 0) {
        if (at > copied) parts.push(video.slice(copied, at));
        parts.push(bytes);
        copied = at + size;
        removed += count;
      }
    }
    at += size;
  }
  if (removed === 0) return { blob: video, removed };
  if (copied < video.size) parts.push(video.slice(copied));
  return { blob: new Blob(parts, { type: video.type }), removed };
}

/** A top-level box's type and size from its first (up to) 16 bytes; `left` is the rest of the file. */
function topLevelBox(header: Uint8Array, left: number): { type: string; size: number } {
  if (header.length < 8) throw new VideoMetadataError();
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
  let size = view.getUint32(0);
  if (size === 1) {
    if (header.length < 16) throw new VideoMetadataError();
    size = Number(view.getBigUint64(8));
  } else if (size === 0) {
    size = left;
  }
  if (size < 8 || size > left) throw new VideoMetadataError();
  return { type: fourcc(header, 4), size };
}

// ------------------------------------------------------------- showing

/** "Photo", "3 photos", "Video", "File", "4 attachments" (mixed kinds). */
export function mediaLabel(kind: MediaKind | "mixed", count: number): string {
  const n = Math.max(1, count);
  if (kind === "mixed") return `${n} attachments`;
  const noun = kind === "image" ? "photo" : kind;
  return n === 1 ? `${noun[0]!.toUpperCase()}${noun.slice(1)}` : `${n} ${noun}s`;
}

/** What a media message's attachments are, as the push payload names them. */
export function attachmentsKind(attachments: readonly { kind: MediaKind }[]): MediaKind | "mixed" | null {
  const kinds = new Set(attachments.map((a) => a.kind));
  if (kinds.size === 0) return null;
  return kinds.size > 1 ? "mixed" : attachments[0]!.kind;
}

/** A media message in one line: "Photo", or "Photo · caption". */
export function mediaText(message: Pick<Message, "kind" | "body" | "attachments">): string {
  const attachments = message.attachments ?? [];
  const kind = attachmentsKind(attachments);
  const label = kind ? mediaLabel(kind, attachments.length) : "Attachment";
  const caption = (message.body ?? "").replace(/\s+/g, " ").trim();
  return caption ? `${label} · ${caption}` : label;
}

/** Columns for a message's photo/video grid. */
export function gridColumns(count: number): number {
  return count <= 1 ? 1 : count === 2 || count === 4 ? 2 : 3;
}

/** A tile's shape from the media's dimensions (square when unknown, kept within 1:2 and 2:1 in a grid). */
export function aspectRatio(media: Pick<Media, "width" | "height">, single: boolean): number {
  if (!media.width || !media.height) return 1;
  if (!single) return 1;
  return Math.min(2, Math.max(0.5, media.width / media.height));
}

/** Whether a signed media URL (`exp` in seconds) has expired, or will within `slackMs`. */
export function urlExpired(url: string, nowMs: number, slackMs = 0): boolean {
  const exp = Number(/[?&]exp=(\d+)/.exec(url)?.[1]);
  return Number.isFinite(exp) && exp * 1000 <= nowMs + slackMs;
}
